import {ReferencesList} from '@github-ui/copilot-chat/components/ChatReferences'
import {WithShimmerEffect} from '@github-ui/copilot-chat/components/WithShimmerEffect'
import {getRenderableReferences} from '@github-ui/copilot-chat/utils/copilot-chat-helpers'
import type {SkillExecution} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {ChevronRightIcon} from '@primer/octicons-react'
import {Spinner} from '@primer/react'
import {useId, useMemo, useState} from 'react'

import {inlineToolCallMetadata} from './inline-tool-call-metadata'
import styles from './InlineToolCall.module.css'

interface InlineToolCallProps {
  skillExecution: SkillExecution
}

export function InlineToolCall({skillExecution}: InlineToolCallProps) {
  const [isOpen, setIsOpen] = useState(false)

  const metadata = useMemo(() => inlineToolCallMetadata(skillExecution), [skillExecution])
  const isPending = skillExecution.status === 'started' || skillExecution.status === 'progress'
  const isError = skillExecution.status === 'error'
  const isComplete = skillExecution.status === 'completed'

  const references = useMemo(
    () => (skillExecution.references ? getRenderableReferences(skillExecution.references) : []),
    [skillExecution.references],
  )

  const hasReferences = references.length > 0

  const logMessage = useMemo(() => {
    if (!metadata) return skillExecution.slug
    // Once the call has produced viewable results, use the reference label
    // ("View file X"). Otherwise the expanded body can only show the call
    // arguments, so use the definition label ("Get file X").
    // For failures, we prefix the definition label with "Failed to …" so the row reflects the error state.
    // The row's status icon/styling also differentiates state.
    if (isComplete && hasReferences && metadata.referenceText) return metadata.referenceText
    if (isError && metadata.definitionText) return `Failed to ${lowercaseFirst(metadata.definitionText)}`
    if (metadata.definitionText) return metadata.definitionText
    if (isPending) return metadata.loadingText
    if (isError) return metadata.failedText ?? `Failed ${metadata.activeSkillText}`
    return metadata.completedText
  }, [metadata, skillExecution.slug, isPending, isComplete, isError, hasReferences])
  const formattedArguments = useMemo(() => formatArguments(skillExecution.arguments), [skillExecution.arguments])
  const detailsMessage = skillExecution.errorMessage || skillExecution.statusMessage
  const hasFallbackDetails = Boolean(formattedArguments || detailsMessage)
  const LeadingIcon = metadata?.icon
  const panelId = useId()
  const titleId = useId()

  return (
    <div className={styles.container} data-active={isPending} data-testid="inline-tool-call">
      <div className={styles.headerContainer}>
        <button
          type="button"
          className={styles.header}
          onClick={() => setIsOpen(prev => !prev)}
          aria-expanded={isOpen}
          aria-controls={panelId}
        >
          {(isPending || LeadingIcon) && (
            <span className={styles.leadingVisual} aria-hidden="true">
              {isPending ? <Spinner size="small" /> : LeadingIcon ? <LeadingIcon /> : null}
            </span>
          )}
          <span className={styles.text}>
            {isPending ? (
              <WithShimmerEffect className={styles.shimmerContent}>
                <span id={titleId} className={styles.title}>
                  {logMessage}
                </span>
              </WithShimmerEffect>
            ) : (
              <span id={titleId} className={styles.title}>
                {logMessage}
              </span>
            )}
          </span>
          <ChevronRightIcon size={12} className={styles.chevron} data-expanded={isOpen} aria-hidden="true" />
        </button>
      </div>
      {isOpen && (
        <div id={panelId} role="region" aria-labelledby={titleId} className={styles.referenceContent}>
          {hasReferences ? (
            <ReferencesList references={references} isImmersive />
          ) : hasFallbackDetails ? (
            <div className={styles.fallbackDetails}>
              {detailsMessage && <p className={styles.detailsMessage}>{detailsMessage}</p>}
              {formattedArguments && <pre className={styles.detailsCode}>{formattedArguments}</pre>}
            </div>
          ) : (
            <p className={styles.detailsMessage}>No details available.</p>
          )}
        </div>
      )}
    </div>
  )
}

function formatArguments(raw: string | undefined): string | undefined {
  if (!raw) return undefined
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>
    // Drop noisy bookkeeping fields so the panel focuses on the actual inputs.
    const rest = Object.fromEntries(Object.entries(parsed).filter(([key]) => key !== 'kind' && key !== 'callId'))
    if (Object.keys(rest).length === 0) return undefined
    return JSON.stringify(rest, null, 2)
  } catch {
    return raw
  }
}

function lowercaseFirst(value: string): string {
  if (!value.length) return value
  return value.charAt(0).toLowerCase() + value.slice(1)
}
